import numberToWords from 'number-to-words'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { Agency } from '@/javascript/interfaces'
import type { ErrorType } from '@/javascript/interfaces'

import {
	FREQUENCY,
	STATUS_CODE,
	TIME,
} from '../helpers/_constants'
import api from '../services/api'
import type {
	AnnualTrainingSchedule,
	ComplianceFrequency,
	Course,
	Curriculum,
	CurriculumData,
	CurriculumError,
	CurriculumTrack,
	Path,
	Requirements,
	RequirementsWithErrors,
	ScheduleData,
	ScheduleValue,
	Topic,
	TopicRequirements,
} from '../services/courses/interfaces'
import {
	type ApiPublishCurriculumPayload,
	type ApiPublishOneoffCurriculumPayload,
	type ApiTrackCreateCopyPayload,
	deleteTrack,
	postCurriculums,
	postTrackCopy,
	putOneoffPublishCurriculum,
	putPublishCurriculum,
	putTrackEdit,
} from '../services/curriculums'
import type { LastPrinted } from '../services/trainingLog/interfaces'
import { useGlobalStore } from '../stores/GlobalStore'
import { useNotificationStore } from '../stores/NotificationStore'
import { deepClone } from '../utils/deepClone'
import { getAgencyId } from '../utils/getAgencyId'
import { capitalize } from '../utils/stringUtils'

interface ModifiedCurriculum {
	courseIds: number[]
	coursesLength: number
	sequence: number
}

export const useCurriculumsStore = defineStore('curriculums', () => {
	// State
	const additionalCurriculumData = ref<CurriculumData>({} as CurriculumData)
	const agency = ref<Agency | null | undefined>(null)
	const annualFrequencyValue = ref<number | null>(null)
	const canChangeCurriculum = ref(false)
	const cantChangeCurriculumWarningText = ref('')
	const currentStep = ref(1)
	const curriculumCount = ref(0)
	const curriculumFrequency = ref<number | null>(null)
	const curriculumId = ref<number | null>(null)
	const curriculumIsDefault = ref(false)
	const curriculumName = ref('')
	const curriculumTracks = ref<CurriculumTrack[]>([])
	const curriculumType = ref(0)
	const curriculums = ref<Curriculum[]>([])
	const deletedCurriculums = ref<Curriculum[]>([])
	const frequency = ref<ComplianceFrequency | number | null>(null)
	const globalStore = useGlobalStore()
	const isCertifications = ref(false)
	const isConfiguration = ref(false)
	const isForCompliance = ref(false)
	const isReordering = ref(false)
	const lastPrinted = ref<LastPrinted>({} as LastPrinted)
	const notificationStore = useNotificationStore()
	const noticeText = ref('')
	const originalCurriculums = ref<Curriculum[]>([])
	const paths = ref<Path>({} as Path)
	const publishingCurriculum = ref(false)
	const reordered = ref(false)
	const requestInProgress = ref(false)
	const requirements = ref<Requirements>({} as Requirements)
	const scheduleData = ref<ScheduleData>({
		classes_count: FREQUENCY.CLASSES_COUNT,
		frequency_count: FREQUENCY.COUNT,
		frequency_type: 'months',
	})
	const scheduleValues = ref<ScheduleValue>({
		immediately: '',
		spread: '',
		when_due_soon: '',
	})
	const selectedCurriculum = ref<Curriculum | null | undefined>(null)
	const selectedCurriculumTrack = ref<CurriculumTrack | null | undefined>(null)
	const selectedScheduleTiming = ref<AnnualTrainingSchedule>('' as AnnualTrainingSchedule)
	const selectedTopicName = ref<number | string>('' as number | string)
	const sidebarCourses = ref<Course[]>([])
	const topics = ref<Topic[]>([])
	const totalSteps = ref(2)

	// Getters
	const complianceRequirements = computed((): RequirementsWithErrors => {
		return !!requirements.value && Object.entries(requirements.value).length && frequency.value !== 'oneoff'
			? {
				...requirements.value,
				topicErrors: curriculums.value.reduce((accumulator, curriculum) => [
					...accumulator,
					...curriculum.errors ?? [],
				], [] as CurriculumError[]),
			}
			: {
				...requirements.value,
				topicErrors: [],
			}
	})

	const hasRequirements = computed(() => {
		return !!(
			complianceRequirements.value?.time ||
			complianceRequirements.value?.topics && !!complianceRequirements.value.topics.length
		)
	})

	const isCertificationsComputed = computed(() => {
		return frequency.value === 'oneoff'
	})

	const isModified = computed(() => {
		return isModifiedCurriculum()
	})

	// Actions
	const addCurriculum = (curriculumFrequency: ComplianceFrequency | number | null) => {
		let curriculumToAdd

		// Add new blank curriculum
		if (!curriculums.value.length) {
			curriculumToAdd = {
				agency_id: getAgencyId(),
				caregiver_type: 0,
				courses: [],
				created_at: '',
				curriculumHeader: getCurriculumHeader(curriculumFrequency, 1),
				curriculum_track_id: curriculumId.value,
				curriculum_type: curriculumType.value,
				description: null,
				duration: 0,
				errors: [],
				frequency: curriculumFrequency,
				id: null,
				is_default: false,
				name: curriculumName.value,
				pricing_tier: globalStore.agency?.pricing_tier ?? 'Tier 3',
				released_at: '',
				sequence: 1,
				updated_at: '',
			}
			// Duplicate previous curriculum
		} else {
			const id = null
			const lastCurriculum = curriculums.value[curriculums.value.length - 1]
			const sequence = lastCurriculum.sequence + 1

			curriculumToAdd = {
				...lastCurriculum,
				courses: lastCurriculum.courses.map(a => ({ ...a })),
				curriculumHeader: getCurriculumHeader(curriculumFrequency, sequence),
				errors: lastCurriculum.errors.map(e => ({ ...e })),
				frequency: curriculumFrequency,
				id,
				sequence,
			}
		}

		curriculums.value.push(curriculumToAdd)
		selectedCurriculum.value = curriculumToAdd
		processCurriculum(curriculumToAdd)
	}

	const copyCurriculumTrack = async () => {
		requestInProgress.value = true

		if (!selectedCurriculumTrack.value) {
			return
		}

		try {
			const params: ApiTrackCreateCopyPayload = {
				agency_id: getAgencyId(),
				name: selectedCurriculumTrack.value.name,
				source_curriculum_track_id: selectedCurriculumTrack.value.id ? selectedCurriculumTrack.value.id : '',
			}

			await postTrackCopy(api, params)

			const message = `"${selectedCurriculumTrack.value.name}" has been copied successfully.`

			notificationStore.onHandleSuccessMessages({ message })
		} catch {
			const message = `There was an error in copying "${selectedCurriculumTrack.value.name}".`

			notificationStore.onHandleErrorMessages({ error: { message } })
		} finally {
			requestInProgress.value = false
		}
	}

	const deleteCurriculumTrack = async ({
		agencyId, id, name,
	}: { agencyId: number, id: number, name: string }) => {
		try {
			await deleteTrack(api, agencyId, id)
			hideDeletedCurriculumTrack(id)

			const message = `${name} has been successfully deleted!`

			notificationStore.onHandleSuccessMessages({ message })
		} catch (error: unknown) {
			if ((error as { response?: { status?: number } }).response?.status === STATUS_CODE.INTERNAL_SERVER_ERROR) {
				const message = 'There was a problem with your request. Please try again later.'

				notificationStore.onHandleErrorMessages({ error: { message } })

				return
			}

			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			globalStore.closeModal()
		}
	}

	const editCurriculumTrack = (curriculum: CurriculumTrack) => {
		hideDeletedCurriculumTrack(curriculum.id)
		showAddedCurriculumTrack(curriculum)
	}

	const formatMinutesAsHoursAndMinutesText = (minutes: number, useAnd: boolean) => {
		const missingHours = Math.floor(minutes / TIME.MINUTES)
		const hoursString = missingHours > 1 ? 'hours' : 'hour'
		const missingMinutes = Math.ceil(minutes % TIME.MINUTES)
		const minutesString = missingMinutes > 1 ? 'minutes' : 'minute'

		if (missingHours && missingMinutes) {
			return `${missingHours} ${hoursString}${useAnd ? ' and' : ','} ${missingMinutes} ${minutesString}`
		} else if (missingHours) {
			return `${missingHours} ${hoursString}`
		} else if (missingMinutes) {
			return `${missingMinutes} ${minutesString}`
		}
	}

	const getCurriculumHeader = (curriculumFrequency: ComplianceFrequency | number | null, sequence: number) => {
		const currentFrequency = curriculumFrequency === 'initial' ? 1 : 2

		return currentFrequency === annualFrequencyValue.value
			? `${capitalize(numberToWords.toWordsOrdinal(sequence))} year team members`
			: ''
	}

	const getCurriculums = async () => {
		if (curriculumId.value) {
			try {
				const params = {
					agency_id: getAgencyId(),
					curriculum_type: curriculumType.value,
					frequency: curriculumFrequency.value || frequency.value,
				}

				const response = await postCurriculums(api, `/api/curriculum/courses/${curriculumId.value}`, params)

				if (response && Array.isArray(response)) {
					for (const curriculum of response) {
						processCurriculum(curriculum)
						curriculums.value.push(curriculum)
					}

					originalCurriculums.value = deepClone(curriculums.value)
					selectedCurriculum.value = curriculums.value[0]
					requirements.value = {
						...requirements.value,
						topicErrors: curriculums.value.reduce((acc, cur) => [
							...acc,
							...cur.errors ?? [],
						], [] as CurriculumError[]),
					}
				}
			} catch (error) {
				const errorMessage = notificationStore.getAPIErrorMessage(error as ErrorType)

				notificationStore.onHandleErrorMessages({ error: errorMessage as ErrorType })
			}
		}

		if (frequency.value === 'oneoff') {
			originalCurriculums.value = deepClone(curriculums.value)
		}
	}

	const getFriendlyTotalDuration = (courses: Course[]) => {
		const durationInMinutes = getTotalDuration(courses)
		const remainder = durationInMinutes % TIME.MINUTES
		const durationInHours = durationInMinutes / TIME.MINUTES

		return `${durationInHours}${remainder > 0 ? '+' : ''} hours`
	}

	const getMissingTimeFromErrors = (errors: CurriculumError[]) => {
		if (errors && errors.length) {
			for (let i = 0; i < errors.length; i++) {
				if (errors[i].type === 'time') {
					return errors[i].value as number
				}
			}
		}

		return 0
	}

	const getTotalDuration = (courses: Course[]) => {
		return courses.reduce((sum: number, course: Course) => sum + parseInt(course.duration_in_mins), 0)
	}

	const goToStep = (stepNum: number) => {
		currentStep.value = stepNum <= 0 ? 1 : stepNum
		// prevent currentStep from going to negative numbers (edge case)
	}

	const hideDeletedCurriculum = (curriculum: Curriculum) => {
		curriculums.value = curriculums.value.filter(c => c.id !== curriculum.id)
	}

	const hideDeletedCurriculumTrack = (id: number) => {
		curriculumTracks.value = curriculumTracks.value.filter(c => c.id !== id)

		if (globalStore.agency) {
			globalStore.agency.curriculumTracks = curriculumTracks.value
		}
	}

	const isModifiedCurriculum = () => {
		const a = JSON.stringify(originalCurriculums.value.map(c => curriculumData(c)))
		const b = JSON.stringify(curriculums.value.map(c => curriculumData(c)))

		return a !== b || reordered.value
	}

	const makeDefaultCurriculum = async (curriculum: CurriculumTrack) => {
		try {
			const params = {
				agency_id: getAgencyId(),
				curriculum_track_id: curriculum.id,
				is_default: curriculum.is_default = !curriculum.is_default,
				name: curriculum.name,
			}

			const response = await putTrackEdit(api, params)

			editCurriculumTrack(response.curriculum_track)

			const message = `${curriculum.name} is the default curriculum`

			notificationStore.onHandleSuccessMessages({ message })
		} catch {
			const message = `There was an error in making ${curriculum.name} the default curriculum.`

			notificationStore.onHandleErrorMessages({ error: { message } })
		}
	}

	const next = () => {
		currentStep.value++
	}

	const previous = () => {
		currentStep.value = currentStep.value <= 1 ? 1 : currentStep.value - 1
		// prevent currentStep from going below 1 (edge case)
	}

	const processCurriculum = (curriculum: Curriculum) => {
		const courses = curriculum.courses || []

		curriculum.curriculumHeader = getCurriculumHeader(curriculum.frequency, curriculum.sequence) ?? ''
		curriculum.duration = getTotalDuration(courses)
		curriculum.errors = [
			...verifyTimeRequirements(curriculum, requirements.value.time),
			...verifyTopicRequirements(courses, requirements.value.topics),
			...verifyNumberOfTopics(courses, requirements.value.topicNumber),
		]

		isModifiedCurriculum()
	}

	const publishCourses = async () => {
		try {
			if (!!deletedCurriculums.value) {
				removeEmptyCurriculums(curriculums.value, deletedCurriculums.value)
			}

			publishingCurriculum.value = true

			if (frequency.value === 'oneoff') {
				const params: ApiPublishOneoffCurriculumPayload = { course_ids: curriculums.value[0].courses.map(c => c.course_id) }

				await putOneoffPublishCurriculum(api, paths.value.update, params)

				const message = `${curriculums.value[0].name} has been updated.`

				notificationStore.onHandleSuccessMessages({ message })
			} else {
				const params: ApiPublishCurriculumPayload = {
					agency_id: getAgencyId(),
					curriculums: JSON.stringify(curriculums.value) ?? '',
					deleted_curriculums: deletedCurriculums.value.length ?
						JSON.stringify(resequenceDeletedCurriculums(curriculums.value.length + 1, deletedCurriculums.value)) : [],
					user_id: globalStore.userId,
				}

				const response = await putPublishCurriculum(api, paths.value.update, params)

				curriculums.value = []
				selectedCurriculum.value = null

				for (const curriculum of response.curriculums) {
					processCurriculum(curriculum)
					curriculums.value.push(curriculum)
				}
				selectedCurriculum.value = curriculums.value.length > 0 ? curriculums.value[0] : null

				notificationStore.onHandleSuccessMessages({ message: response.message })
			}

			globalStore.clearSelectedValues()
			globalStore.resetCAModal()

			originalCurriculums.value = deepClone(curriculums.value)
			reordered.value = false
		} catch (error) {
			const apiErrorMessage = notificationStore.getAPIErrorMessage(error as ErrorType)

			notificationStore.onHandleErrorMessages({ error: apiErrorMessage as ErrorType })
		} finally {
			publishingCurriculum.value = false
		}
	}

	const removeEmptyCurriculums = (curriculumsToProcess: Curriculum[], deletedCurriculumsToProcess: Curriculum[]) => {
		for (let i = 0; i < curriculumsToProcess.length; i++) {
			if (curriculumsToProcess[i].courses.length === 0) {
				deletedCurriculumsToProcess.push(curriculumsToProcess[i])
				curriculumsToProcess.splice(i, 1)

				for (let j = i; j < curriculumsToProcess.length; j++) {
					curriculumsToProcess[j].sequence = j + 1
				}
			}
		}
	}

	const renameCurriculum = async (name: string) => {
		requestInProgress.value = true

		if (!selectedCurriculumTrack.value) {
			return
		}

		try {
			const params = {
				agency_id: getAgencyId(),
				curriculum_track_id: selectedCurriculumTrack.value.id,
				is_default: selectedCurriculumTrack.value.is_default,
				name,
			}

			const response = await putTrackEdit(api, params)

			if ('curriculum_track' in response) {
				editCurriculumTrack(response.curriculum_track)
			}
		} catch {
			const message = `There was an error in renaming "${curriculumName.value}".`

			notificationStore.onHandleErrorMessages({ error: { message } })
		} finally {
			requestInProgress.value = false
		}
	}

	const resequenceDeletedCurriculums = (start: number, deletedCurriculumsToProcess: Curriculum[]): Curriculum[] => {
		let sequence = start

		for (const curriculum of deletedCurriculumsToProcess) {
			curriculum.sequence = sequence++

			// Remove courses and errors from deleted curriculums
			curriculum.courses = []
			curriculum.errors = []
		}

		return deletedCurriculumsToProcess
	}

	const resetData = () => {
		currentStep.value = 1
		curriculumCount.value = 0
		curriculumFrequency.value = null
		curriculumId.value = null
		curriculumName.value = ''
		curriculumType.value = 0
		selectedCurriculumTrack.value = {} as CurriculumTrack
		globalStore.resetCAModal()
	}

	const resetSchedulerData = () => {
		scheduleData.value = {
			classes_count: FREQUENCY.CLASSES_COUNT,
			frequency_count: FREQUENCY.COUNT,
			frequency_type: 'months',
		} as ScheduleData
	}

	const setSelectedCurriculumTrack = (value: CurriculumTrack | null | undefined) => {
		if (value) {
			selectedCurriculumTrack.value = value
			curriculumId.value = value.id
			curriculumName.value = value.name
		}
	}

	const showAddedCurriculumTrack = (curriculum: CurriculumTrack) => {
		if (curriculum.is_default) {
			curriculumTracks.value.forEach(c => c.is_default = false)
			curriculumTracks.value.unshift(curriculum)
		} else {
			curriculumTracks.value.push(curriculum)
		}

		if (globalStore.agency) {
			globalStore.agency.curriculumTracks = curriculumTracks.value
		}

		selectedCurriculumTrack.value = curriculum
	}

	const verifyNumberOfTopics = (courses: Course[], numberOfTopics: number): CurriculumError[] => {
		const errors: CurriculumError[] = []

		// Iterate over curriculum courses to get all curriculum's topics
		let topicsArray: Topic[] = []

		for (let i = 0; i < courses.length; i++) {
			topicsArray = topicsArray.concat(courses[i].topics as Topic[])
		}

		// Get unique curriculum's topics
		const uniqueTopics = topicsArray.filter(function (item, i, ar) {
			return item !== undefined && ar.indexOf(item) === i
		})

		// Check whether curriculum topics requirements are fulfilled or not
		if (uniqueTopics && uniqueTopics.length < numberOfTopics) {
			errors.push({
				type: 'topics-number',
				value: numberOfTopics - uniqueTopics.length,
			})
		}

		return errors
	}

	const verifyTimeRequirements = (curriculum: Curriculum, timeRequirements: number): CurriculumError[] => {
		const errors: CurriculumError[] = []
		const duration = curriculum.duration ? curriculum.duration : 0

		if (timeRequirements * TIME.MINUTES > duration) {
			errors.push({
				type: 'time',
				value: timeRequirements * TIME.MINUTES - duration,
			})
		}

		return errors
	}

	const verifyTopicRequirements = (courses: Course[], topicRequirements: TopicRequirements[]): CurriculumError[] => {
		const errors: CurriculumError[] = []

		if (!topicRequirements || !topicRequirements.length) {
			return errors
		}

		// Deduplicate by topic_id to prevent duplicate alerts when multiple
		// TopicRequirement records reference the same topic
		const seenTopicIds = new Set<number>()
		const uniqueTopicRequirements = topicRequirements.filter(requirement => {
			if (seenTopicIds.has(requirement.topic_id)) {
				return false
			}

			seenTopicIds.add(requirement.topic_id)

			return true
		})

		// Generate an error for each required topic not covered by any course in the curriculum
		for (let i = 0; i < uniqueTopicRequirements.length; i++) {
			let isFound = false

			for (let j = 0; j < courses.length; j++) {
				const topicsArray = courses[j].topics

				if (!Array.isArray(topicsArray) || !topicsArray.length) {
					continue
				}

				if (topicsArray.find(topic => topic.id === uniqueTopicRequirements[i].topic_id)) {
					isFound = true
					break
				}
			}

			if (!isFound) {
				errors.push({
					id: uniqueTopicRequirements[i].id,
					type: 'topic',
					value: uniqueTopicRequirements[i].name,
				})
			}
		}

		return errors
	}

	return {
		// State
		additionalCurriculumData,
		agency,
		annualFrequencyValue,
		canChangeCurriculum,
		cantChangeCurriculumWarningText,
		currentStep,
		curriculumCount,
		curriculumFrequency,
		curriculumId,
		curriculumIsDefault,
		curriculumName,
		curriculumTracks,
		curriculumType,
		curriculums,
		deletedCurriculums,
		frequency,
		isCertifications,
		isConfiguration,
		isForCompliance,
		isReordering,
		lastPrinted,
		noticeText,
		originalCurriculums,
		paths,
		publishingCurriculum,
		reordered,
		requestInProgress,
		requirements,
		scheduleData,
		scheduleValues,
		selectedCurriculum,
		selectedCurriculumTrack,
		selectedScheduleTiming,
		selectedTopicName,
		sidebarCourses,
		topics,
		totalSteps,

		// Getters
		complianceRequirements,
		hasRequirements,
		isCertificationsComputed,
		isModified,

		// Actions
		addCurriculum,
		copyCurriculumTrack,
		deleteCurriculumTrack,
		editCurriculumTrack,
		formatMinutesAsHoursAndMinutesText,
		getCurriculumHeader,
		getCurriculums,
		getFriendlyTotalDuration,
		getMissingTimeFromErrors,
		getTotalDuration,
		goToStep,
		hideDeletedCurriculum,
		hideDeletedCurriculumTrack,
		isModifiedCurriculum,
		makeDefaultCurriculum,
		next,
		previous,
		processCurriculum,
		publishCourses,
		removeEmptyCurriculums,
		renameCurriculum,
		resequenceDeletedCurriculums,
		resetData,
		resetSchedulerData,
		setSelectedCurriculumTrack,
		showAddedCurriculumTrack,
		verifyNumberOfTopics,
		verifyTimeRequirements,
		verifyTopicRequirements,
	}
})

function curriculumData(curriculum: Curriculum): ModifiedCurriculum {
	return {
		courseIds: curriculum.courses?.map(c => c.course_id),
		coursesLength: curriculum.courses?.length,
		sequence: curriculum.sequence,
	}
}

export default useCurriculumsStore
